import { inject, Service } from '@angular/core';
import type { PlaceOrderResponseDto, ProductDto } from '@api/models';
import { Language } from '@api/models';
import { DeviceService } from './device-detector/device.service';

// Both are installed by their vendor snippets in index.html and ship no types.
declare const gtag: (command: string, action: string, params?: Record<string, unknown>) => void;
declare const fbq: (command: 'track', event: string, params?: Record<string, unknown>) => void;

export interface ProductAnalyticsData {
  sku: string;
  name: string;
  price: number;
  source?: string;
  qty?: number;
}

@Service()
export class GoogleAnalyticsService {
  private deviceService = inject(DeviceService);

  viewProduct(product: ProductDto) {
    if (!this.deviceService.isPlatformBrowser()) {
      return;
    }

    // todo Add 1.'ecomm_category' 2.serverside USD conversion 3. isSaleItem: false 4. same event for 'cart'!! and maybe ecomm_pagetype searchresults/category/purchase
    this.trackEvent('page_view', {
      send_to: 'AW-930099759',
      ecomm_pagetype: 'product',
      ecomm_prodid: product.sku,
      ecomm_totalvalue: product.price ? product.price / 28.0 : 1.0,
    });

    this.trackEvent('view_item', {
      items: [this.buildItem(product)]
    });

    fbq('track', 'ViewContent', {
      content_ids: [product.sku],
      value: product.price,
      currency: 'UAH',
      content_type: 'product'
    });
  }

  addToCart(data: ProductAnalyticsData): void {
    if (!this.deviceService.isPlatformBrowser()) {
      return;
    }

    this.trackEvent('add_to_cart', {
      currency: 'UAH',
      items: [this.buildItem(data)],
      value: data.price,
      custom_source: data.source,
    });

    fbq('track', 'AddToCart', {
      content_ids: [data.sku],
      value: data.price * data.qty,
      currency: 'UAH',
      content_type: 'product'
    });
  }

  removeFromCart(data: ProductAnalyticsData): void {
    const qty = data.qty ? data.qty : 1;
    this.trackEvent('remove_from_cart', {
      currency: 'UAH',
      items: [this.buildItem(data)],
      value: data.price * qty,
      custom_source: data.source,
    });
  }

  showCart(source: string) {
    this.trackEvent('custom_show_cart', { source: source });
  }

  tryConfirmOrder(totalCost: number) {
    this.trackEvent('custom_try_confirm_order', { total_cost: totalCost });
  }

  trackOrderPlaced(response: PlaceOrderResponseDto) {
    if (!this.deviceService.isPlatformBrowser()) {
      return;
    }

    const totalCost = response.order.prices.totalCost;

    gtag('event', 'conversion', {
      send_to: 'AW-930099759/az7OCJeAt8QBEK_kwLsD',
      value: totalCost,
      currency: 'UAH',
      transaction_id: response.order.id
    });

    this.trackEvent('purchase', {
      currency: 'UAH',
      items: response.order.items.map(item => {
        return {
          id: item.sku,
          name: item.name,
          price: item.price,
          currency: 'UAH',
          quantity: item.qty
        };
      }),
      transaction_id: response.order.id,
      value: totalCost,
      coupon: response.order.promoCode?.name,
      customer_type: response.isReturningCustomer ? 'returning' : 'new',
    });

    fbq('track', 'Purchase', {
      value: totalCost,
      currency: 'UAH',
      contents: response.order.items.map(item => {
        return {
          id: item.sku,
          quantity: item.qty
        };
      }),
      content_type: 'product'
    });
  }

  changeLang(lang: Language) {
    this.trackEvent('custom_change_lang', { new_lang: lang });
  }

  addToWishlist(data: ProductAnalyticsData): void {
    this.trackEvent('add_to_wishlist', {
      currency: 'UAH',
      value: data.price,
      items: [{
        id: data.sku,
        name: data.name,
        price: data.price,
        currency: 'UAH',
      }],
      custom_source: data.source,
    });
  }

  removeFromWishlist(data: ProductAnalyticsData): void {
    this.trackEvent('custom_remove_from_wishlist', {
      id: data.sku,
      name: data.name,
      price: data.price,
      source: data.source,
    });
  }

  beginCheckout(productsData: ProductAnalyticsData[]): void {
    const totalValue = productsData.reduce((acc, product) => acc + product.price * product.qty, 0);

    this.trackEvent('begin_checkout', {
      currency: 'UAH',
      value: totalValue,
      items: productsData.map(product => ({
        id: product.sku,
        name: product.name,
        price: product.price,
        currency: 'UAH',
        quantity: product.qty,
      })),
    });
  }

  private trackEvent(name: string, params: Record<string, unknown>) {
    if (!this.deviceService.isPlatformBrowser()) {
      return;
    }

    gtag('event', name, params);
  }

  private buildItem(productData: ProductAnalyticsData): Record<string, unknown> {
    return {
      item_id: productData.sku,
      item_name: productData.name,
      price: productData.price,
      quantity: productData.qty,
    };
  }
}
